/**
 * ScopeThreeActivityForm — entry form for Scope 3 Cat 6 / Cat 7.
 *
 * Design notes:
 *  - Multi-leg: a single trip may have several legs (flight LHR→JFK + taxi +
 *    rail). Each leg picks its own mode and discriminators. Hitting "Save all
 *    legs" persists each leg as its own ghg_activity row, so they compose on
 *    the dashboard and show up as separate auditable entries.
 *
 *  - Factor picker (per leg): instead of a flat "specific factor" dropdown,
 *    the user picks a transport mode, and the form surfaces the right
 *    discriminators for that mode — cabin class, fuel type, size, RF, etc.
 *    The single matching DEFRA factor is resolved automatically and shown
 *    as a small badge.
 *
 *  - Origin / Destination use the same autocomplete as the shipment
 *    calculator (/api/locations/search, /api/airports/search) and pass the
 *    resolved lat/lng through to the backend so it doesn't re-geocode.
 *
 *  - Travelers field: shown only when the factor is per passenger-km. Bumping
 *    it up multiplies emissions (3 employees on the same flight leg → 3× the
 *    per-person figure). For per-vehicle km factors (car / motorbike) we
 *    hide the field because a shared car still emits the same amount.
 *
 *  - hotel (Cat 6) and homeworking (Cat 7) stay out of the transport dropdown
 *    and live as optional add-on cards below.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Plane, Car, Hotel, Home, Users, Bike, Train, Bus, Ship,
  Plus, Trash2, ArrowRight,
} from 'lucide-react';
import {
  getFactorCatalog, calculatePreview, saveActivity,
} from '../../services/scope3Service';
import LocationAutocomplete from './LocationAutocomplete';

const CATEGORY_META = {
  6: { label: 'Business Travel', icon: Plane, activityType: 'business_travel' },
  7: { label: 'Employee Commute', icon: Car, activityType: 'employee_commute' },
};

const NON_TRANSPORT_MODES = new Set(['hotel', 'homeworking']);

// ---------------------------------------------------------------------------
// Per-mode discriminator schema
// ---------------------------------------------------------------------------
const MODE_CONFIG = {
  car: {
    icon: Car, label: 'Car',
    discriminators: ['size_class', 'fuel_type'],
    preferActivityGroup: 'size',    // prefer "Cars (by size)" over "by market segment"
    defaultActivityUnit: 'km',
  },
  motorbike: {
    icon: Bike, label: 'Motorbike',
    discriminators: ['size_class'],
    defaultActivityUnit: 'km',
  },
  taxi: {
    icon: Car, label: 'Taxi',
    discriminators: ['vehicle_type'],
    defaultActivityUnit: 'passenger.km',
  },
  bus: {
    icon: Bus, label: 'Bus / Coach',
    discriminators: ['vehicle_type'],
    defaultActivityUnit: 'passenger.km',
  },
  rail: {
    icon: Train, label: 'Train / Rail',
    discriminators: ['vehicle_type'],
    defaultActivityUnit: 'passenger.km',
  },
  flight: {
    icon: Plane, label: 'Flight',
    discriminators: ['vehicle_type', 'cabin_class', 'radiative_forcing'],
    defaultActivityUnit: 'passenger.km',
  },
  ferry: {
    icon: Ship, label: 'Ferry',
    discriminators: ['vehicle_type'],
    defaultActivityUnit: 'passenger.km',
  },
};

const VALUE_LABELS = {
  size_class: {
    'Small car': 'Small', 'Medium car': 'Medium', 'Large car': 'Large',
    'Average car': 'Average / not sure', 'Small': 'Small',
    'Medium': 'Medium', 'Large': 'Large', 'Average': 'Average',
  },
  fuel_type: {
    diesel: 'Diesel', petrol: 'Petrol', hybrid: 'Hybrid',
    phev: 'Plug-in hybrid (PHEV)', bev: 'Battery electric (EV)',
    cng: 'CNG', lpg: 'LPG', unknown: 'Not sure / fleet average',
  },
  cabin_class: {
    'Average passenger': 'Average cabin',
    'Economy class': 'Economy',
    'Premium economy class': 'Premium economy',
    'Business class': 'Business',
    'First class': 'First',
  },
};

const DISCRIMINATOR_LABEL = {
  size_class: 'Size',
  fuel_type: 'Fuel',
  cabin_class: 'Cabin class',
  vehicle_type: 'Type',
  radiative_forcing: 'Radiative forcing',
};

function valueLabel(field, value) {
  if (field === 'radiative_forcing') {
    if (value === true || value === 1) return 'Include RF (recommended)';
    if (value === false || value === 0) return 'Exclude RF';
  }
  return (VALUE_LABELS[field] && VALUE_LABELS[field][value]) || String(value);
}

// Flatten a mode's groups into a single factor list
function flattenMode(catalogModes, mode) {
  const groups = catalogModes?.[mode] || {};
  const out = [];
  for (const g of Object.keys(groups)) {
    for (const f of groups[g]) {
      out.push({ ...f, _group: g });
    }
  }
  return out;
}

// For cars, DEFRA has TWO groupings — "by size" (Small/Med/Large/Average) and
// "by market segment" (Mini/Supermini/etc). We prefer the simpler by-size list.
function preferredFactors(catalogModes, mode) {
  const cfg = MODE_CONFIG[mode] || {};
  const all = flattenMode(catalogModes, mode);
  if (!cfg.preferActivityGroup) return all;
  const preferred = all.filter((f) => (f._group || '').toLowerCase().includes(cfg.preferActivityGroup));
  return preferred.length ? preferred : all;
}

// Collect distinct values for a discriminator across the filtered factor list
function distinctValues(factors, field) {
  const seen = new Set();
  const out = [];
  for (const f of factors) {
    const v = f[field];
    if (v === null || v === undefined) continue;
    const key = String(v);
    if (!seen.has(key)) { seen.add(key); out.push(v); }
  }
  return out;
}

// Narrow a factor list by partial picks
function applyPicks(factors, picks) {
  return factors.filter((f) => {
    for (const k of Object.keys(picks)) {
      const p = picks[k];
      if (p === '' || p === null || p === undefined) continue;
      if (k === 'radiative_forcing') {
        const pBool = p === 'true';
        if (!!f[k] !== pBool) return false;
      } else if (String(f[k]) !== String(p)) {
        return false;
      }
    }
    return true;
  });
}

// Default picks: Average/Medium for size, 'unknown' for fuel, RF=true for flights
function defaultPicks(mode, factors) {
  const picks = {};
  const values = {};
  for (const d of (MODE_CONFIG[mode]?.discriminators || [])) {
    values[d] = distinctValues(factors, d);
  }
  if (mode === 'car') {
    if (values.size_class?.some((v) => /average/i.test(v))) {
      picks.size_class = values.size_class.find((v) => /average/i.test(v));
    }
    if (values.fuel_type?.includes('unknown')) picks.fuel_type = 'unknown';
  } else if (mode === 'motorbike') {
    if (values.size_class?.some((v) => /average/i.test(v))) {
      picks.size_class = values.size_class.find((v) => /average/i.test(v));
    }
  } else if (mode === 'flight') {
    picks.radiative_forcing = 'true';
  }
  return picks;
}

// Travelers: whole number 1–999 (empty / invalid → 1)
function normTravelers(v) {
  const n = Math.floor(Number(v));
  return Number.isFinite(n) && n >= 1 ? Math.min(n, 999) : 1;
}

// ---------------------------------------------------------------------------
// Per-leg state
// ---------------------------------------------------------------------------
function newLeg() {
  return {
    id: `leg-${Math.random().toString(36).slice(2, 9)}`,
    mode: '',
    picks: {},             // { size_class, fuel_type, vehicle_type, cabin_class, radiative_forcing }
    value: '',             // distance (string for Input)
    originLoc: null,
    originQ: '',
    destLoc: null,
    destQ: '',
    travelers: 1,
    preview: null,
  };
}

// ---------------------------------------------------------------------------
// Main form
// ---------------------------------------------------------------------------
export default function ScopeThreeActivityForm({ ghgCategory, prefill, onSaved }) {
  const meta = CATEGORY_META[ghgCategory] || CATEGORY_META[6];
  const Icon = meta.icon;

  const [catalog, setCatalog] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [legs, setLegs] = useState([newLeg()]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [purpose, setPurpose] = useState('');
  const [saving, setSaving] = useState(false);

  const loadCatalog = React.useCallback(() => {
    setLoadError(null);
    setCatalog(null);
    getFactorCatalog({ ghg_category: ghgCategory })
      .then((data) => {
        if (!data || !data.modes) {
          setLoadError({ msg: 'Response missing "modes"', preview: JSON.stringify(data).slice(0, 300) });
          return;
        }
        setCatalog(data);
      })
      .catch((err) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const msg = err?.message || 'Unknown error';
        // eslint-disable-next-line no-console
        console.error('[Scope3] factor-catalog fetch failed', { status, detail, err });
        setLoadError({ status, detail, msg,
          url: `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/api/scope3/factor-catalog?ghg_category=${ghgCategory}` });
      });
  }, [ghgCategory]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  // Reset legs and chat prefill when category flips
  useEffect(() => {
    setLegs([newLeg()]);
  }, [ghgCategory]);

  // Apply a chat-proposal prefill into the first leg (best-effort)
  useEffect(() => {
    if (!prefill) return;
    setLegs((arr) => {
      const [first, ...rest] = arr;
      const next = { ...first };
      if (prefill.mode) next.mode = prefill.mode;
      if (prefill.activity_value != null) next.value = String(prefill.activity_value);
      if (prefill.origin) next.originQ = prefill.origin;
      if (prefill.destination) next.destQ = prefill.destination;
      const picks = { ...next.picks };
      if (prefill.size_class) picks.size_class = prefill.size_class;
      if (prefill.fuel_type) picks.fuel_type = prefill.fuel_type;
      if (prefill.cabin_class) picks.cabin_class = prefill.cabin_class;
      if (prefill.radiative_forcing != null) picks.radiative_forcing = String(prefill.radiative_forcing);
      if (prefill.vehicle_type) picks.vehicle_type = prefill.vehicle_type;
      next.picks = picks;
      if (prefill.passengers != null) next.travelers = prefill.passengers;
      return [next, ...rest];
    });
    if (prefill.purpose) setPurpose(prefill.purpose);
  }, [prefill]);

  const allModes = catalog?.modes || {};
  const transportModes = Object.keys(allModes).filter((m) => !NON_TRANSPORT_MODES.has(m));

  // -------- leg ops --------
  const updateLeg = (id, patch) =>
    setLegs((arr) => arr.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const addLeg = () => setLegs((arr) => [...arr, newLeg()]);
  const removeLeg = (id) => setLegs((arr) => arr.filter((l) => l.id !== id));

  // Build the activity payload for one leg.
  //
  // Trick: the user thinks in *distance* (one-leg km), not per-passenger-km.
  // DEFRA factors for flights / rail / bus / taxi / ferry are published per
  // passenger.km, so we always send `activity_unit='km'` for travel modes —
  // the backend's _compute branch converts km → passenger.km and multiplies
  // by `passengers`. This makes "3 travelers × 5567 km flight" = 16701 pkm
  // regardless of whether the user typed the distance or let the backend
  // auto-resolve it from origin+destination.
  function buildPayload(leg, factor) {
    const travelers = normTravelers(leg.travelers);
    const rawValue = leg.value ? Number(leg.value) : null;
    // km is the universal travel-leg unit the user works with
    const activity_unit = 'km';
    return {
      ghg_category: ghgCategory,
      activity_type: meta.activityType,
      mode: leg.mode,
      activity_value: rawValue,
      activity_unit,
      passengers: travelers,
      vehicle_type: factor.vehicle_type,
      size_class: factor.size_class,
      fuel_type: factor.fuel_type,
      cabin_class: factor.cabin_class,
      radiative_forcing: factor.radiative_forcing,
      region: factor.region,
      origin: (leg.originLoc?.address || leg.originQ || '').trim() || undefined,
      destination: (leg.destLoc?.address || leg.destQ || '').trim() || undefined,
      origin_lat: leg.originLoc?.latitude,
      origin_lng: leg.originLoc?.longitude,
      destination_lat: leg.destLoc?.latitude,
      destination_lng: leg.destLoc?.longitude,
      activity_date: date || undefined,
      purpose: purpose || undefined,
    };
  }

  // Why a leg can't be calculated yet, in plain words (null = ready).
  function legProblem(leg, idx) {
    const name = `Leg ${idx + 1}${leg.mode ? ` (${MODE_CONFIG[leg.mode]?.label || leg.mode})` : ''}`;
    if (!leg.mode) return `${name}: pick a transport mode.`;
    if (!resolveFactor(leg, allModes).factor) return `${name}: pick the remaining options (size, fuel, class…).`;
    const hasDistance = Number(leg.value) > 0;
    const hasOrigin = !!(leg.originLoc || (leg.originQ || '').trim());
    const hasDest = !!(leg.destLoc || (leg.destQ || '').trim());
    if (leg.value !== '' && !hasDistance) return `${name}: distance must be greater than 0.`;
    if (!hasDistance && !(hasOrigin && hasDest)) {
      if (hasOrigin && !hasDest) return `${name}: add a destination, or type the distance.`;
      if (!hasOrigin && hasDest) return `${name}: add an origin, or type the distance.`;
      return `${name}: enter origin and destination, or type the distance.`;
    }
    return null;
  }

  async function handlePreviewAll() {
    const out = [];
    for (const [idx, leg] of legs.entries()) {
      const res = resolveFactor(leg, allModes);
      const problem = legProblem(leg, idx);
      if (problem) { out.push({ ...leg, preview: null }); toast.error(problem); continue; }
      try {
        const preview = await calculatePreview(buildPayload(leg, res.factor));
        out.push({ ...leg, preview });
      } catch (e) {
        out.push({ ...leg, preview: null });
        toast.error(`Leg ${idx + 1}: ${e?.response?.data?.detail || e?.detail || 'preview failed'}`);
      }
    }
    setLegs(out);
  }

  async function handleSaveAll() {
    const problems = legs.map((l, i) => legProblem(l, i)).filter(Boolean);
    if (problems.length) {
      // Don't half-save a trip: tell the user exactly what each leg still needs.
      problems.forEach((p) => toast.error(p));
      return;
    }
    const saveable = legs;
    setSaving(true);
    let ok = 0, totalKg = 0;
    try {
      for (const leg of saveable) {
        const { factor } = resolveFactor(leg, allModes);
        try {
          const res = await saveActivity(buildPayload(leg, factor));
          ok += 1;
          totalKg += Number(res.co2e_kg) || 0;
        } catch (e) {
          toast.error(`Leg save failed: ${e?.response?.data?.detail || e?.detail || 'unknown'}`);
        }
      }
      if (ok > 0) {
        toast.success(`Saved ${ok} leg${ok === 1 ? '' : 's'} — ${totalKg.toFixed(2)} kg CO₂e`);
        setLegs([newLeg()]);
        onSaved?.({ saved: ok, totalKg });
      }
    } finally {
      setSaving(false);
    }
  }

  // -------- render --------
  if (loadError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-red-700">Could not load DEFRA emission factors</CardTitle>
          <CardDescription>Details below.</CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="text-xs bg-slate-50 p-3 rounded border overflow-auto max-h-60">
{JSON.stringify(loadError, null, 2)}
          </pre>
          <div className="mt-3 flex gap-2">
            <Button onClick={loadCatalog}>Retry</Button>
            {loadError.url && (
              <Button variant="outline" onClick={() => window.open(loadError.url, '_blank')}>
                Open endpoint directly
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }
  if (!catalog) return <p className="text-slate-500 p-4">Loading UK DESNZ 2026 factors…</p>;

  const totalPreview = legs.reduce((sum, l) => sum + (l.preview?.co2e_kg || 0), 0);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5" /> {meta.label} — Scope 3 Cat {ghgCategory}
          </CardTitle>
          <CardDescription>
            Add one leg per mode of travel. Distance auto-calculates when you pick origin &amp; destination.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Trip-level fields */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Trip date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Label>Purpose (optional)</Label>
              <Input value={purpose} onChange={(e) => setPurpose(e.target.value)}
                     placeholder="e.g. Client visit, Q2 offsite" />
            </div>
          </div>

          {/* Legs */}
          {legs.map((leg, idx) => (
            <LegEditor
              key={leg.id}
              index={idx}
              leg={leg}
              allModes={allModes}
              transportModes={transportModes}
              canRemove={legs.length > 1}
              onChange={(patch) => updateLeg(leg.id, patch)}
              onRemove={() => removeLeg(leg.id)}
            />
          ))}

          <div className="flex flex-wrap gap-2 items-center">
            <Button variant="outline" onClick={addLeg}>
              <Plus className="w-4 h-4 mr-1" /> Add another leg
            </Button>
            <Button variant="outline" onClick={handlePreviewAll}>
              Preview all legs
            </Button>
            <Button onClick={handleSaveAll} disabled={saving}>
              {saving ? 'Saving…' : `Calculate & Save ${legs.length} leg${legs.length === 1 ? '' : 's'}`}
            </Button>
          </div>

          {totalPreview > 0 && (
            <div className="rounded-lg border p-4 bg-emerald-50">
              <div className="text-2xl font-semibold text-emerald-900">
                {totalPreview.toFixed(3)} kg CO₂e total
              </div>
              <div className="text-xs text-emerald-800">
                {legs.filter((l) => l.preview).length} leg(s) previewed.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Optional add-ons */}
      {ghgCategory === 6 && allModes['hotel'] && (
        <OptionalHotelCard groups={allModes['hotel']} onSaved={onSaved} />
      )}
      {ghgCategory === 7 && allModes['homeworking'] && (
        <OptionalHomeworkingCard groups={allModes['homeworking']} onSaved={onSaved} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Resolve the single DEFRA factor best matching a leg's mode + discriminator picks
// ---------------------------------------------------------------------------
function resolveFactor(leg, allModes) {
  if (!leg.mode) return { factor: null, candidates: [] };
  const candidates = preferredFactors(allModes, leg.mode);
  const narrowed = applyPicks(candidates, leg.picks);
  if (narrowed.length === 0) return { factor: null, candidates };
  // Prefer km-based factors for car/motorbike (DEFRA ships both km and miles rows)
  const cfg = MODE_CONFIG[leg.mode] || {};
  const unitPref = cfg.defaultActivityUnit || 'km';
  const byUnit = narrowed.filter((f) => f.activity_unit === unitPref);
  const picked = (byUnit.length ? byUnit : narrowed)[0];
  return { factor: picked, candidates: narrowed };
}

// ---------------------------------------------------------------------------
// LegEditor — one card per leg
// ---------------------------------------------------------------------------
function LegEditor({ index, leg, allModes, transportModes, canRemove, onChange, onRemove }) {
  const cfg = leg.mode ? MODE_CONFIG[leg.mode] : null;
  const ModeIcon = cfg?.icon || ArrowRight;

  // When mode changes, seed default picks + reset addresses/value
  const setMode = (m) => {
    const factors = preferredFactors(allModes, m);
    const picks = defaultPicks(m, factors);
    onChange({ mode: m, picks, preview: null });
  };

  const setPick = (field, v) => {
    onChange({
      picks: { ...leg.picks, [field]: v === '__any__' ? '' : v },
      preview: null,
    });
  };

  const factors = useMemo(() => preferredFactors(allModes, leg.mode), [allModes, leg.mode]);
  const { factor, candidates } = useMemo(() => resolveFactor(leg, allModes), [leg, allModes]);

  // Values available for each discriminator — narrowed by other picks
  const availableValues = (field) => {
    const otherPicks = { ...leg.picks };
    delete otherPicks[field];
    return distinctValues(applyPicks(factors, otherPicks), field);
  };

  const isPassengerFactor = factor?.activity_unit === 'passenger.km';
  const travelers = normTravelers(leg.travelers);

  return (
    <Card className="border bg-white">
      <CardHeader className="py-3 flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center text-xs font-semibold">
            {index + 1}
          </div>
          <ModeIcon className="w-4 h-4 text-slate-500" />
          <CardTitle className="text-base">
            Leg {index + 1}{cfg ? ` — ${cfg.label}` : ''}
          </CardTitle>
        </div>
        {canRemove && (
          <Button size="icon" variant="ghost" onClick={onRemove} title="Remove this leg">
            <Trash2 className="w-4 h-4 text-red-600" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Mode */}
        <div>
          <Label>Transport mode</Label>
          <Select value={leg.mode} onValueChange={setMode}>
            <SelectTrigger><SelectValue placeholder="Pick a mode (car, flight, rail, …)" /></SelectTrigger>
            <SelectContent>
              {transportModes.map((m) => {
                const MI = (MODE_CONFIG[m]?.icon) || ArrowRight;
                return (
                  <SelectItem key={m} value={m}>
                    <span className="inline-flex items-center gap-2">
                      <MI className="w-3.5 h-3.5 text-slate-500" />
                      {MODE_CONFIG[m]?.label || m}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Per-mode discriminators */}
        {cfg && (
          <div className={`grid gap-3 grid-cols-1 md:grid-cols-${Math.min(cfg.discriminators.length, 3)}`}>
            {cfg.discriminators.map((field) => {
              const values = availableValues(field);
              if (values.length === 0) return null;
              return (
                <div key={field}>
                  <Label>{DISCRIMINATOR_LABEL[field] || field}</Label>
                  <Select
                    value={leg.picks[field] != null ? String(leg.picks[field]) : ''}
                    onValueChange={(v) => setPick(field, v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={`Pick ${DISCRIMINATOR_LABEL[field] || field}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {values.map((v) => (
                        <SelectItem key={String(v)} value={String(v)}>
                          {valueLabel(field, v)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
        )}

        {/* Resolved factor badge */}
        {cfg && (
          factor ? (
            <div className="text-xs bg-sky-50 border border-sky-100 rounded-md px-3 py-2 text-sky-900">
              <span className="font-medium">Using factor:</span>{' '}
              {factor.display_name}{' '}
              <Badge variant="outline" className="ml-1">{factor.co2e_total} kg CO₂e / {factor.activity_unit}</Badge>
            </div>
          ) : (
            <div className="text-xs bg-amber-50 border border-amber-100 rounded-md px-3 py-2 text-amber-900">
              {candidates.length === 0
                ? 'No DEFRA factor matches these picks. Adjust the fields above.'
                : 'Ambiguous — pick one more option to narrow down.'}
            </div>
          )
        )}

        {/* Origin / Destination + distance + travelers */}
        {cfg && (
          <div className="space-y-3 bg-slate-50 border rounded-lg p-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <LocationAutocomplete
                label="Origin"
                mode={leg.mode}
                queryText={leg.originQ}
                value={leg.originLoc}
                onQueryChange={(q) => {
                  const patch = { originQ: q, preview: null };
                  if (leg.originLoc && q !== leg.originLoc.address) patch.originLoc = null;
                  onChange(patch);
                }}
                onSelect={(loc) => onChange({ originLoc: loc, originQ: loc.address, preview: null })}
                testIdPrefix={`leg${index}-origin`}
              />
              <LocationAutocomplete
                label="Destination"
                mode={leg.mode}
                queryText={leg.destQ}
                value={leg.destLoc}
                onQueryChange={(q) => {
                  const patch = { destQ: q, preview: null };
                  if (leg.destLoc && q !== leg.destLoc.address) patch.destLoc = null;
                  onChange(patch);
                }}
                onSelect={(loc) => onChange({ destLoc: loc, destQ: loc.address, preview: null })}
                testIdPrefix={`leg${index}-dest`}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>
                  Distance ({factor?.activity_unit || cfg.defaultActivityUnit}){' '}
                  <span className="text-xs text-slate-400 font-normal">— optional if addresses filled</span>
                </Label>
                <Input type="number" step="any" value={leg.value}
                       onChange={(e) => onChange({ value: e.target.value, preview: null })}
                       placeholder="0" />
              </div>
              {isPassengerFactor && (
                <div>
                  <Label className="flex items-center gap-1"><Users className="w-3 h-3" /> Travelers</Label>
                  {/* Keep the raw text while typing (an emptied box must stay empty, otherwise
                      clearing it snaps to "1" and typing 3 produces 13). Normalise on blur. */}
                  <Input type="number" min="1" max="999" step="1" inputMode="numeric" value={leg.travelers}
                         onChange={(e) => onChange({ travelers: e.target.value, preview: null })}
                         onBlur={() => onChange({ travelers: normTravelers(leg.travelers) })} />
                  <p className="text-[10px] text-slate-500 mt-1">
                    Multiplies emissions (factor is per passenger-km).
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Leg preview */}
        {leg.preview && (
          <div className="text-sm bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-emerald-900">
            <span className="font-semibold">{Number(leg.preview.co2e_kg || 0).toFixed(2)} kg CO₂e</span>
            {' · '}
            {Number(leg.preview.activity_value || 0).toFixed(1)} {leg.preview.activity_unit}
            {isPassengerFactor && travelers > 1 && ` (${travelers} travelers)`}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// OptionalHotelCard — unchanged from before
// ---------------------------------------------------------------------------
function OptionalHotelCard({ groups, onSaved }) {
  const options = useMemo(() => {
    const out = [];
    for (const g of Object.keys(groups)) for (const f of groups[g]) out.push(f);
    return out;
  }, [groups]);

  const [countryIdx, setCountryIdx] = useState('');
  const [nights, setNights] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  const selected = countryIdx !== '' ? options[Number(countryIdx)] : null;
  const canCalc = selected && Number(nights) > 0;

  const build = () => ({
    ghg_category: 6, activity_type: 'business_travel', mode: 'hotel',
    activity_value: Number(nights), activity_unit: selected.activity_unit,
    vehicle_type: selected.vehicle_type, region: selected.region,
    activity_date: date || undefined,
  });
  const handlePreview = async () => {
    if (!canCalc) return;
    try { setPreview(await calculatePreview(build())); }
    catch (e) { toast.error(e?.response?.data?.detail || e?.detail || 'Preview failed'); }
  };
  const handleSave = async () => {
    if (!canCalc) return;
    setSaving(true);
    try {
      const res = await saveActivity(build());
      toast.success(`Hotel saved: ${Number(res.co2e_kg || 0).toFixed(2)} kg CO₂e`);
      setPreview(res); onSaved?.(res); setNights('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-dashed">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Hotel className="w-4 h-4 text-amber-600" /> Optional — Hotel stay
        </CardTitle>
        <CardDescription>Non-transport accommodation. Independent of the legs above.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <Label>Country / region</Label>
            <Select value={countryIdx} onValueChange={setCountryIdx}>
              <SelectTrigger><SelectValue placeholder="Pick a country" /></SelectTrigger>
              <SelectContent>
                {options.map((f, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {f.region} — {f.co2e_total} kg/{f.activity_unit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nights</Label>
            <Input type="number" min="1" step="1" value={nights}
                   onChange={(e) => setNights(e.target.value)} placeholder="e.g. 3" />
          </div>
          <div>
            <Label>Stay date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePreview} disabled={!canCalc}>Preview</Button>
          <Button onClick={handleSave} disabled={!canCalc || saving}>
            {saving ? 'Saving…' : 'Save hotel stay'}
          </Button>
        </div>
        {preview && (
          <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-2">
            {Number(preview.co2e_kg || 0).toFixed(2)} kg CO₂e · {preview.factor_used?.display_name}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// OptionalHomeworkingCard
// ---------------------------------------------------------------------------
function OptionalHomeworkingCard({ groups, onSaved }) {
  const options = useMemo(() => {
    const out = [];
    for (const g of Object.keys(groups)) for (const f of groups[g]) out.push(f);
    return out;
  }, [groups]);

  // Pre-select the combined (office equipment + heating) factor. The old placeholder text looked
  // like a selected value while nothing was selected, so Preview/Save stayed disabled silently.
  const defaultIdx = useMemo(() => {
    const i = options.findIndex((f) => /combined|both|equipment.*heating|heating.*equipment/i
      .test(`${f.vehicle_type || ''} ${f.display_name || ''}`));
    return options.length ? String(i >= 0 ? i : 0) : '';
  }, [options]);
  const [factorIdx, setFactorIdx] = useState(defaultIdx);
  useEffect(() => { if (factorIdx === '' && defaultIdx !== '') setFactorIdx(defaultIdx); }, [defaultIdx, factorIdx]);
  const [daysPerWeek, setDaysPerWeek] = useState(2);
  const [weeks, setWeeks] = useState(48);
  const [hoursPerDay, setHoursPerDay] = useState(8);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  const selected = factorIdx !== '' ? options[Number(factorIdx)] : null;
  const fteHours = Number(daysPerWeek) * Number(hoursPerDay) * Number(weeks);
  const canCalc = selected && fteHours > 0;

  const build = () => ({
    ghg_category: 7, activity_type: 'homeworking', mode: 'homeworking',
    activity_value: fteHours, activity_unit: selected.activity_unit,
    vehicle_type: selected.vehicle_type, region: selected.region,
  });
  const handlePreview = async () => {
    if (!canCalc) return;
    try { setPreview(await calculatePreview(build())); }
    catch (e) { toast.error(e?.response?.data?.detail || e?.detail || 'Preview failed'); }
  };
  const handleSave = async () => {
    if (!canCalc) return;
    setSaving(true);
    try {
      const res = await saveActivity(build());
      toast.success(`Homeworking saved: ${Number(res.co2e_kg || 0).toFixed(2)} kg CO₂e`);
      setPreview(res); onSaved?.(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Card className="border-dashed">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Home className="w-4 h-4 text-teal-600" /> Optional — Working from home
        </CardTitle>
        <CardDescription>DEFRA homeworking factor (office equipment + heating).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Factor</Label>
            <Select value={factorIdx} onValueChange={setFactorIdx}>
              <SelectTrigger><SelectValue placeholder="Pick a homeworking factor" /></SelectTrigger>
              <SelectContent>
                {options.map((f, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {f.vehicle_type} — {f.co2e_total} kg/{f.activity_unit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div />
          <div>
            <Label>WFH days / week</Label>
            <Input type="number" min="0" max="7" step="0.5"
                   value={daysPerWeek} onChange={(e) => setDaysPerWeek(e.target.value)} />
          </div>
          <div>
            <Label>Hours / day</Label>
            <Input type="number" min="1" max="24" step="0.5"
                   value={hoursPerDay} onChange={(e) => setHoursPerDay(e.target.value)} />
          </div>
          <div>
            <Label>Weeks covered</Label>
            <Input type="number" min="1" max="52" step="1"
                   value={weeks} onChange={(e) => setWeeks(e.target.value)} />
          </div>
          <div className="flex items-end">
            <div className="text-xs text-slate-500">
              Annual FTE hours: <span className="font-medium text-slate-700">{fteHours || 0}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePreview} disabled={!canCalc}>Preview</Button>
          <Button onClick={handleSave} disabled={!canCalc || saving}>
            {saving ? 'Saving…' : 'Save WFH footprint'}
          </Button>
        </div>
        {preview && (
          <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-2">
            {Number(preview.co2e_kg || 0).toFixed(2)} kg CO₂e · {preview.factor_used?.display_name}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
